// A photo's pictures for the screen: its thumbnail (the strip, and the
// preview's stand-in) and its full size (the preview). Object URLs made here
// are released when the photo changes or the screen goes.
import { useEffect, useState } from 'react';
import type { ShownPhoto } from './area-photos';
import { getPhotoContent } from './api';
import type { DevicePhoto } from './device-db';
import { previewSource, type FullSize } from './photo-preview';
import { remoteThumbnail } from './remote-thumbnails';

/** How long the full size may take from SharePoint before the preview settles for the thumbnail. */
const FULL_SIZE_TIMEOUT_MS = 20_000;

/** The device's own thumbnail. Thumbnails are data: URLs; only photos taken before that change hold a Blob. */
function useDeviceThumbnail(device: DevicePhoto | null): string | undefined {
  const [blobUrl, setBlobUrl] = useState<string>();
  const thumbnail = device?.thumbnail;
  useEffect(() => {
    if (!thumbnail || typeof thumbnail === 'string') return;
    const objectUrl = URL.createObjectURL(thumbnail);
    setBlobUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [thumbnail]);
  return typeof thumbnail === 'string' ? thumbnail : blobUrl;
}

/** A photo taken on another device: its thumbnail from SharePoint; null once it can't be fetched. */
function useRemoteThumbnail(photo: ShownPhoto): string | null | undefined {
  const [url, setUrl] = useState<string | null>();
  useEffect(() => {
    if (photo.device) return;
    let current = true;
    void remoteThumbnail(photo.areaId, photo.id).then((u) => current && setUrl(u));
    return () => {
      current = false;
    };
  }, [photo.areaId, photo.id, photo.device]);
  return url;
}

/** The photo's thumbnail: undefined while it loads, null when it can't be had (another device's photo, no signal). */
export function usePhotoThumbnail(photo: ShownPhoto): string | null | undefined {
  const deviceUrl = useDeviceThumbnail(photo.device);
  const remoteUrl = useRemoteThumbnail(photo);
  return photo.device ? deviceUrl : remoteUrl;
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error('Timed out')), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

/** The photo at full size: from the device while it waits to upload, else from SharePoint when there's signal. */
export function useFullSizePhoto(photo: ShownPhoto, online: boolean): FullSize {
  const source = previewSource(photo.device, online);
  const image = photo.device?.image ?? null;
  const [full, setFull] = useState<FullSize>({ status: 'loading' });
  useEffect(() => {
    if (source === 'thumbnail') {
      setFull({ status: 'unavailable', reason: 'offline' });
      return;
    }
    let current = true;
    let objectUrl: string | undefined;
    setFull({ status: 'loading' });
    const blob = source === 'device' && image ? Promise.resolve(image) : withTimeout(getPhotoContent(photo.areaId, photo.id), FULL_SIZE_TIMEOUT_MS);
    blob.then(
      (b) => {
        if (!current) return;
        objectUrl = URL.createObjectURL(b);
        setFull({ status: 'ready', url: objectUrl });
      },
      () => current && setFull({ status: 'unavailable', reason: 'failed' }),
    );
    return () => {
      current = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [source, image, photo.areaId, photo.id]);
  return full;
}
